import type {
	BIRequest,
	FilterRuleModel,
	Metric,
	StatisticsInterpretabilityResponse,
} from '@/shared/generated'
import type { TFunction } from 'i18next'
import type { MetricsConfig, ValueTypes } from '../lib/helpers'

import { useEffect, useMemo } from 'react'
import { any, filter, map, pipe, prop } from 'lodash/fp'
import { AlertTriangleIcon, SearchXIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	useBIAnalyticQuery,
	useBIRevisionQuery,
	useMetricsQuery,
} from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'
import { useOptimizationQuery } from '@/entities/optimization'
import {
	MetricDataType,
	useInterpretabilityStatisticQuery,
	useRepricingStatisticQuery,
} from '@/entities/optimization-statistic'

import { Operators } from '@/shared/components/query-builder'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/shared/components/ui/accordion'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { useOptimizationGroupId } from '@/shared/hooks/useOptimzationGroupId'
import { ampli } from '@/shared/lib/ampli'

import {
	getForecastPeriod,
	getMetricAnalytic,
	getMetricCurrency,
	getMetricForecast,
	getMetricsAnalyticData,
	getPeriod,
	getPrefixedMetric,
	getTypesByMetricSchema,
	METRIC_PREFIX,
	sortMetricsConfigByStrategy,
} from '../lib/helpers'
import { MetricsPredictionChart } from './metrics-prediction'

export default function ProgressWidgets({
	filters,
}: {
	filters: FilterRuleModel[]
}) {
	const { t } = useTranslation()

	useEffect(() => {
		ampli.optSummaryOpenProgressTab()
	}, [])
	const currency = useSelectedCurrency()
	const optimizationGroupId = useOptimizationGroupId()!
	const defaultFilter = {
		name: 'optimization_group_id',
		operation: Operators.IS,
		value: optimizationGroupId,
	} as FilterRuleModel
	const optimizationQuery = useOptimizationQuery(optimizationGroupId)
	const metricsQuery = useMetricsQuery({
		select: filter(({ type }) => type === 'predict_default'),
	})
	const repricingStatisticQuery = useRepricingStatisticQuery(
		{
			optimizations: [
				{
					optimization_id: optimizationQuery.data!.id,
					optimization_group_id: optimizationGroupId,
				},
			],
			filters: [],
		},
		{
			enabled: !!optimizationGroupId && !!optimizationQuery.data?.id,
			select: prop('settings.strategy'),
		}
	)
	const interpretabilityStatisticQuery =
		useInterpretabilityStatisticQuery<StatisticsInterpretabilityResponse>(
			{
				optimizations: [
					{
						optimization_id: optimizationQuery.data!.id,
						optimization_group_id: optimizationGroupId,
					},
				],
				filters,
				currency,
			},
			{
				enabled: !!optimizationQuery.data!.id,
			}
		)
	const biRevisionDateQuery = useBIRevisionQuery<string>({
		refetchOnMount: 'always',
		select: (data) => data?.['date'] as string,
	})
	const metricsConfig = useMemo(
		() => getMetricsConfig({ metricsSchema: metricsQuery.data!, currency }, t),
		[metricsQuery.data, currency]
	)
	const biAnalyticBody: BIRequest = useMemo(
		() => ({
			date_aggregation: 'date_key',
			metrics: map(prop('analyticKey'))(metricsConfig),
			product_filters: [defaultFilter, ...filters],
			filters: [],
			currency,
			...getPeriod(biRevisionDateQuery.data),
		}),
		[biRevisionDateQuery.data, filters, currency]
	)
	const biAnalyticQuery = useBIAnalyticQuery(biAnalyticBody, {
		enabled: !!biRevisionDateQuery.data,
	})
	const isError = any(prop('isError'), [
		biRevisionDateQuery,
		biAnalyticQuery,
		interpretabilityStatisticQuery,
		repricingStatisticQuery,
		metricsQuery,
	])
	const isLoading = any(prop('isLoading'), [
		biRevisionDateQuery,
		biAnalyticQuery,
		interpretabilityStatisticQuery,
		repricingStatisticQuery,
		metricsQuery,
	])
	const analyticsData = useMemo(
		() => getMetricsAnalyticData(biAnalyticQuery.data!),
		[biAnalyticQuery.data]
	)
	const enrichMetricsConfig = (config: MetricsConfig[]) => {
		const forecastPeriod = getForecastPeriod(biRevisionDateQuery.data)

		return config?.map(
			({
				analyticKey,
				prefixedKey,
				metricValueTypes,
				...rest
			}: MetricsConfig) => {
				const metricKey = `${prefixedKey}.final`
				const forecastData =
					(
						interpretabilityStatisticQuery.data?.metrics as MetricsDataStatistic
					)?.[metricKey] || 0
				/** The 'final' value, returned for the margin metric, represents a raw value and needs to be multiplied by 100 to express it as a percentage */
				const isMetricDataTypePercent =
					metricValueTypes.dataType === MetricDataType.Percent
				const formattedForcastData = isMetricDataTypePercent
					? forecastData * 100
					: forecastData

				return {
					...rest,
					analyticKey,
					metricValueTypes,
					data: [
						...getMetricAnalytic(analyticKey, analyticsData),
						isMetricDataTypePercent
							? [forecastPeriod, formattedForcastData]
							: getMetricForecast(forecastPeriod, formattedForcastData),
					],
				}
			}
		)
	}
	const metrics: MetricType[] = useMemo(() => {
		const strategy = repricingStatisticQuery.data

		return (
			strategy &&
			pipe(sortMetricsConfigByStrategy, enrichMetricsConfig)(
				metricsConfig,
				strategy,
				t
			)
		)
	}, [
		analyticsData,
		interpretabilityStatisticQuery.data,
		repricingStatisticQuery.data,
		t,
	])
	if (isError) {
		return (
			<div className='flex h-full items-center justify-center'>
				<Empty>
					<EmptyHeader>
						<EmptyMedia variant='icon'>
							<AlertTriangleIcon />
						</EmptyMedia>
						<EmptyTitle>{t('fatal_error_title')}</EmptyTitle>
						<EmptyDescription>{t('fatal_error_desc')}</EmptyDescription>
					</EmptyHeader>
				</Empty>
			</div>
		)
	}
	if (!isLoading && !biRevisionDateQuery.data) {
		return (
			<div className='flex h-full items-center justify-center'>
				<Empty>
					<EmptyHeader>
						<EmptyMedia variant='icon'>
							<SearchXIcon />
						</EmptyMedia>
						<EmptyDescription>
							{t('opt.summary.not_available_for_rb', {
								defaultValue:
									'This tab is not available for rule-based groups.',
							})}
						</EmptyDescription>
					</EmptyHeader>
				</Empty>
			</div>
		)
	}

	return (
		<div className='py-4 pb-10'>
			<Accordion
				defaultValue={metrics?.map((metric, i) => `${metric.title}_${i}`)}
				type='multiple'
			>
				{metrics?.map((metric, i) => (
					<AccordionItem
						value={`${metric.title}_${i}`}
						key={`${metric.title}_${i}`}
					>
						<AccordionTrigger>
							<div className='grid gap-1'>
								<h5 className='text-base'>{metric.title}</h5>
								<p className='text-sm font-normal text-muted-foreground'>
									{metric.subtitle}
								</p>
							</div>
						</AccordionTrigger>
						<AccordionContent>
							{isLoading && <Skeleton className='h-60 w-full rounded-lg' />}
							{!isLoading && !!analyticsData?.length && (
								<MetricsPredictionChart
									metrics={[
										{
											name: metric.name,
											data: metric.data,
											metricValueTypes: metric.metricValueTypes,
											currency: metric.currency,
										},
									]}
								/>
							)}
						</AccordionContent>
					</AccordionItem>
				))}
			</Accordion>
		</div>
	)
}

const METRIC_KEYS = [
	'gross_profit',
	'gross_profit_margin',
	'sales_items',
	'revenue',
]
const getMetricsConfig = (
	{
		metricsSchema,
		currency,
	}: {
		metricsSchema: Metric[]
		currency?: string
	},
	t: TFunction
): MetricsConfig[] =>
	METRIC_KEYS.map((key: string) => {
		const prefixedKey = getPrefixedMetric({
			metric: key,
			prefix: METRIC_PREFIX,
		})
		const metricSchema = metricsSchema?.find(
			({ name }) => name.split('.')[0] === `${prefixedKey}`
		)

		return {
			analyticKey: key,
			metricValueTypes: getTypesByMetricSchema(metricSchema!),
			name: t(`metric.${key}`),
			prefixedKey,
			currency: getMetricCurrency(metricSchema, currency),
		}
	})

type MetricType = {
	analyticKey: string
	title: string
	subtitle: string
	data: [number, number][]
	name: string
	metricValueTypes: ValueTypes
	currency?: string
}
type MetricsDataStatistic = Record<string, number>
